import type { GenUiKey, NextServiceId } from '@voll-intelligence/genui-contract';

/** O `t` que o pedido usa: só texto e número interpolados. */
export type NextServiceTranslate = (
  key: GenUiKey,
  values?: Record<string, string | number>,
) => string;

/** Data "YYYY-MM-DD…" → exibição por locale; `null` quando não parseia OU não é
 *  um dia de calendário real (2026-02-31, mês 13…) — o provider http não
 *  garante ISO, e a cláusula de datas degrada, nunca quebra nem inventa data. */
export function askDate(raw: string | undefined, locale: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw ?? '');
  if (!m) return null;
  const [, y = '', mo = '', d = ''] = m;
  // setUTCFullYear em vez de Date.UTC: Date.UTC remapeia anos 0000–0099 para
  // 1900–1999, e o round-trip reprovaria datas válidas como 0000-02-29.
  const roundTrip = new Date(0);
  roundTrip.setUTCFullYear(Number(y), Number(mo) - 1, Number(d));
  if (roundTrip.getUTCMonth() !== Number(mo) - 1 || roundTrip.getUTCDate() !== Number(d)) {
    return null;
  }
  return locale.startsWith('en') ? `${y}-${mo}-${d}` : `${d}/${mo}/${y}`;
}

/**
 * Onde e quando a pessoa fica, tirado do carrinho do turno (anti-N+1): o que
 * cada produto sabe. `param` é o nome da interpolação na copy (`{{city}}`,
 * `{{hotel}}`, `{{place}}`); `value` nulo = não se sabe onde, e o pedido fala
 * de «essa viagem». As datas já vêm formatadas (`askDate`).
 */
export interface NextServiceAnchor {
  param: 'city' | 'hotel' | 'place';
  value: string | null;
  from: string | null;
  to: string | null;
}

/**
 * Como uma linha compõe o seu pedido:
 * - `fixed`: o texto não depende da viagem;
 * - `anchored`: cita só o lugar; sem lugar, `trip`;
 * - `window`: cita o lugar e a janela, degradando por partes — ida e volta →
 *   `range`; só a ida → `open`; sem data → `bare`; sem lugar (ou sem `bare`)
 *   → `trip`.
 */
export type NextServiceAskRule =
  | { fixed: GenUiKey }
  | { anchored: GenUiKey; trip?: GenUiKey }
  | { window: { range: GenUiKey; open: GenUiKey; bare?: GenUiKey; trip?: GenUiKey } };

export type NextServiceAskRules = Readonly<Record<NextServiceId, NextServiceAskRule>>;

/** A chave de degradação ausente é um erro de contrato: o produto declarou
 *  uma âncora que pode faltar sem dizer o que pedir no lugar. */
function required(key: GenUiKey | undefined): GenUiKey {
  if (key === undefined) throw new Error('next_services: regra de pedido sem degradação');
  return key;
}

function windowAsk(
  keys: Extract<NextServiceAskRule, { window: unknown }>['window'],
  anchor: NextServiceAnchor,
  t: NextServiceTranslate,
): string {
  const { param, value, from, to } = anchor;
  if (value === null) return t(required(keys.trip));
  if (from !== null && to !== null) return t(keys.range, { [param]: value, from, to });
  if (from !== null) return t(keys.open, { [param]: value, from });
  if (keys.bare) return t(keys.bare, { [param]: value });
  return t(required(keys.trip));
}

/** O pedido visível que a linha manda na PRÓPRIA conversa (ação `reply`). */
export function composeNextServiceAsk(
  rule: NextServiceAskRule,
  anchor: () => NextServiceAnchor,
  t: NextServiceTranslate,
): string {
  if ('fixed' in rule) return t(rule.fixed);
  const resolved = anchor();
  if ('window' in rule) return windowAsk(rule.window, resolved, t);
  if (resolved.value === null) return t(required(rule.trip));
  return t(rule.anchored, { [resolved.param]: resolved.value });
}
